import { useAttachmentUrl } from '@/features/messages';
import { PhotoCamera } from '@mui/icons-material';
import { Avatar, type AvatarProps, IconButton } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { getAvatarColors, getInitials } from './gradient-avatar.style';

interface GradientAvatarProps extends Omit<AvatarProps, 'onChange'> {
  peerId?: string;
  backgroundColor?: string;
  name?: string;
  enableUpload?: boolean;
  isOnline?: boolean;
  onChange?: (url: File) => void;
  bucketName?: string;
}

export const GradientAvatar = ({
  peerId,
  backgroundColor,
  name,
  src,
  enableUpload = false,
  isOnline = false,
  onChange,
  sx,
  bucketName,
  ...props
}: GradientAvatarProps) => {
  const [c1, c2] = getAvatarColors(peerId, backgroundColor);
  const [isLoaded, setIsLoaded] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { blobUrl: imageUrl, isError } = useAttachmentUrl(typeof src === 'string' ? src : undefined, true, bucketName);

  useEffect(() => {
    if (imageUrl && !isError) {
      const img = new Image();
      img.onload = () => setIsLoaded(true);
      img.onerror = () => setIsLoaded(false);
      img.src = imageUrl;
    } else {
      setIsLoaded(false);
    }
  }, [imageUrl, isError]);

  const handleClick = () => {
    if (enableUpload) {
      inputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    try {
      onChange?.(file);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    } finally {
      e.target.value = '';
    }
  };

  const avatarSrc = previewUrl || (imageUrl && !isError ? imageUrl : undefined);

  if (enableUpload) {
    return (
      <div className='mex:relative mex:inline-block'>
        <Avatar
          {...props}
          src={avatarSrc}
          onClick={handleClick}
          sx={{
            color: 'common.white',
            fontWeight: 'bold',
            background: isLoaded && !previewUrl ? 'none' : `linear-gradient(135deg, ${c1}, ${c2})`,
            fontSize: '1rem',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8
            },
            ...sx
          }}
        >
          {getInitials(name)}
        </Avatar>

        <IconButton
          size='small'
          onClick={handleClick}
          disabled={false}
          sx={{
            position: 'absolute',
            bottom: -8,
            right: -8,
            backgroundColor: 'primary.main',
            color: 'common.white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            '&.Mui-disabled': {
              backgroundColor: 'grey.400',
              color: 'grey.600'
            },
            width: 24,
            height: 24,
            fontSize: '0.75rem'
          }}
        >
          <PhotoCamera fontSize='inherit' />
        </IconButton>

        <input type='file' accept='image/*' ref={inputRef} style={{ display: 'none' }} onChange={handleFileChange} />
      </div>
    );
  }

  return (
    <Avatar
      {...props}
      src={avatarSrc}
      sx={{
        color: 'common.white',
        fontWeight: 'bold',
        background: isLoaded && !previewUrl ? 'none' : `linear-gradient(135deg, ${c1}, ${c2})`,
        fontSize: '1rem',
        ...sx
      }}
    >
      {getInitials(name)}
    </Avatar>
  );
};
